import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  Save,
  RefreshCw,
  Building,
  Mail,
  Phone,
  MapPin,
  Globe,
  DollarSign,
  Percent,
  Bell,
  Shield,
  Database,
  Printer
} from 'lucide-react';

const GeneralSettings = () => {
  const { user } = useAuth();
  const { updateSettings } = useSettings();
  const navigate = useNavigate();

  // UI State
  const [loading, setLoading] = useState(false);

  // Settings State
  const [settings, setSettings] = useState({
    // Company Information
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyAddress: '',
    companyWebsite: '',
    taxNumber: '',
    
    // Business Settings
    currency: 'GHS',
    currencySymbol: 'Ghs',
    taxRate: '0',
    taxEnabled: false,
    
    // System Settings
    lowStockAlert: '10',
    receiptFooter: '',
    autoBackup: true,
    posViewType: '1',
    requireOtpForDelete: true,
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: false,
    lowStockNotifications: true,
  });


  // Load settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getSettings();
      if (response.success && response.data) {
        // Map database field names to frontend state
        const mappedSettings = {
          companyName: response.data.company_name || '',
          companyEmail: response.data.company_email || '',
          companyPhone: response.data.company_phone || '',
          companyAddress: response.data.company_address || '',
          companyWebsite: response.data.company_website || '',
          taxNumber: response.data.tax_number || '',
          currency: response.data.currency || 'GHS',
          currencySymbol: response.data.currency_symbol || 'Ghs',
          taxRate: response.data.tax_rate?.toString() || '0',
          taxEnabled: response.data.tax_enabled || false,
          lowStockAlert: response.data.low_stock_alert?.toString() || '10',
          receiptFooter: response.data.receipt_footer || '',
          autoBackup: response.data.auto_backup !== undefined ? response.data.auto_backup : true,
          posViewType: response.data.pos_view_type?.toString() || '1',
          requireOtpForDelete: response.data.require_otp_for_delete !== undefined ? response.data.require_otp_for_delete : true,
          emailNotifications: response.data.email_notifications !== undefined ? response.data.email_notifications : true,
          smsNotifications: response.data.sms_notifications || false,
          lowStockNotifications: response.data.low_stock_notifications !== undefined ? response.data.low_stock_notifications : true,
        };
        
          setSettings(mappedSettings);
        }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Fallback to localStorage if database fails
      try {
        const savedSettings = localStorage.getItem('nexapos_settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          setSettings(prev => ({
            ...prev,
              ...parsedSettings
            }));
          }
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      // Prepare the settings data for saving to database
      const settingsData = {
        company_name: settings.companyName || null,
        company_email: settings.companyEmail || null,
        company_phone: settings.companyPhone || null,
        company_address: settings.companyAddress || null,
        company_website: settings.companyWebsite || null,
        tax_number: settings.taxNumber || null,
        currency: settings.currency || null,
        currency_symbol: settings.currencySymbol || null,
        tax_rate: settings.taxRate ? parseFloat(settings.taxRate) : null,
        tax_enabled: Boolean(settings.taxEnabled),
        low_stock_alert: settings.lowStockAlert ? parseInt(settings.lowStockAlert) : null,
        receipt_footer: settings.receiptFooter || null,
        auto_backup: Boolean(settings.autoBackup),
        pos_view_type: settings.posViewType ? parseInt(settings.posViewType) : 1,
        require_otp_for_delete: Boolean(settings.requireOtpForDelete),
        email_notifications: Boolean(settings.emailNotifications),
        sms_notifications: Boolean(settings.smsNotifications),
        low_stock_notifications: Boolean(settings.lowStockNotifications),
      };

      const response = await updateSettings(settingsData);
      
      if (response.success) {
        // Also save to localStorage as backup
        const settingsToSave = {
          companyName: settings.companyName,
          companyEmail: settings.companyEmail,
          companyPhone: settings.companyPhone,
          companyAddress: settings.companyAddress,
          companyWebsite: settings.companyWebsite,
          taxNumber: settings.taxNumber,
          currency: settings.currency,
          currencySymbol: settings.currencySymbol,
          taxRate: settings.taxRate,
          taxEnabled: settings.taxEnabled,
          lowStockAlert: settings.lowStockAlert,
          receiptFooter: settings.receiptFooter,
          autoBackup: settings.autoBackup,
          posViewType: settings.posViewType,
          requireOtpForDelete: settings.requireOtpForDelete,
          emailNotifications: settings.emailNotifications,
          smsNotifications: settings.smsNotifications,
          lowStockNotifications: settings.lowStockNotifications,
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem('nexapos_settings', JSON.stringify(settingsToSave));
        
        toast.success('Settings saved successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
      } else {
        // Show specific validation errors if available
        if (response.errors) {
          const errorMessages = Object.values(response.errors).flat();
          throw new Error(`Validation failed: ${errorMessages.join(', ')}`);
        }
        throw new Error(response.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      console.error('Error details:', error.message);
      
      // Fallback to localStorage if database save fails
      try {
        const settingsToSave = {
          companyName: settings.companyName,
          companyEmail: settings.companyEmail,
          companyPhone: settings.companyPhone,
          companyAddress: settings.companyAddress,
          companyWebsite: settings.companyWebsite,
          taxNumber: settings.taxNumber,
          currency: settings.currency,
          currencySymbol: settings.currencySymbol,
          taxRate: settings.taxRate,
          taxEnabled: settings.taxEnabled,
          lowStockAlert: settings.lowStockAlert,
          receiptFooter: settings.receiptFooter,
          autoBackup: settings.autoBackup,
          posViewType: settings.posViewType,
          requireOtpForDelete: settings.requireOtpForDelete,
          emailNotifications: settings.emailNotifications,
          smsNotifications: settings.smsNotifications,
          lowStockNotifications: settings.lowStockNotifications,
          lastUpdated: new Date().toISOString()
        };
        localStorage.setItem('nexapos_settings', JSON.stringify(settingsToSave));
        
        toast.success('Settings saved locally (database unavailable)', {
          duration: 4000,
          icon: '⚠️',
          style: {
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: '#fff',
          },
        });
      } catch (localError) {
        console.error('Error saving to localStorage:', localError);
        toast.error(`Failed to save settings: ${error.message}`, {
          duration: 5000,
          icon: '❌',
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: '#fff',
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <Layout>
      <div className="space-y-6">
            {/* Page Header */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mt-16 sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                    General Settings
                  </h1>
                  <p className="text-gray-600 text-sm sm:text-base mt-2">Configure your system preferences and business information</p>
                </div>
                <button
                  onClick={handleSaveSettings}
                  disabled={loading}
                  className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Settings Form */}
            <div className="space-y-6">
              {/* Company Information */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-6 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                    <Building className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Company Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={settings.companyName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        name="companyEmail"
                        value={settings.companyEmail}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="company@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="tel"
                        name="companyPhone"
                        value={settings.companyPhone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="+233 123 456 789"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tax Number</label>
                    <div className="relative">
                      <Shield className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        name="taxNumber"
                        value={settings.taxNumber}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="C1234567890"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="url"
                        name="companyWebsite"
                        value={settings.companyWebsite}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="www.example.com"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <textarea
                        name="companyAddress"
                        value={settings.companyAddress}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Company physical address"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Settings */}
              <div className="bg-gradient-to-br from-white via-green-50/30 to-emerald-50/50 rounded-xl p-6 shadow-lg border border-green-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Business Settings</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                    <select
                      name="currency"
                      value={settings.currency}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="GHS">GHS - Ghanaian Cedi</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Currency Symbol</label>
                    <input
                      type="text"
                      name="currencySymbol"
                      value={settings.currencySymbol}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Ghs"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%)</label>
                    <div className="relative">
                      <Percent className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        name="taxRate"
                        value={settings.taxRate}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="taxEnabled"
                        checked={settings.taxEnabled}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable Tax Calculation</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* System Settings */}
              <div className="bg-gradient-to-br from-white via-purple-50/30 to-pink-50/50 rounded-xl p-6 shadow-lg border border-purple-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <Database className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">System Settings</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Low Stock Alert Threshold</label>
                    <input
                      type="number"
                      name="lowStockAlert"
                      value={settings.lowStockAlert}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="10"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="autoBackup"
                        checked={settings.autoBackup}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Enable Auto Backup</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">POS View Type</label>
                    <div className="relative">
                      <select
                        name="posViewType"
                        value={settings.posViewType}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 appearance-none bg-white"
                      >
                        <option value="1">View 1 - Standard Layout</option>
                        <option value="2">View 2 - Compact Layout</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="requireOtpForDelete"
                        checked={settings.requireOtpForDelete}
                        onChange={handleInputChange}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Require OTP for Delete/Remove</span>
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Receipt Footer Text</label>
                    <div className="relative">
                      <Printer className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                      <textarea
                        name="receiptFooter"
                        value={settings.receiptFooter}
                        onChange={handleInputChange}
                        rows="2"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        placeholder="Thank you for your business!"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-gradient-to-br from-white via-orange-50/30 to-amber-50/50 rounded-xl p-6 shadow-lg border border-orange-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-amber-600 rounded-lg flex items-center justify-center mr-3">
                    <Bell className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Notification Preferences</h3>
                </div>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-3 bg-white/50 rounded-lg cursor-pointer hover:bg-white/80 transition-colors">
                    <span className="text-sm font-medium text-gray-700">Email Notifications</span>
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={settings.emailNotifications}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-white/50 rounded-lg cursor-pointer hover:bg-white/80 transition-colors">
                    <span className="text-sm font-medium text-gray-700">SMS Notifications</span>
                    <input
                      type="checkbox"
                      name="smsNotifications"
                      checked={settings.smsNotifications}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                  </label>
                  <label className="flex items-center justify-between p-3 bg-white/50 rounded-lg cursor-pointer hover:bg-white/80 transition-colors">
                    <span className="text-sm font-medium text-gray-700">Low Stock Alerts</span>
                    <input
                      type="checkbox"
                      name="lowStockNotifications"
                      checked={settings.lowStockNotifications}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

    </Layout>
  );
};

export default GeneralSettings;
